import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { UserData } from '../../types';

const PLAN_PRICES: Record<string, { month: number; annual: number }> = {
  essential:    { month: 50, annual: 42 },
  professional: { month: 90, annual: 75 },
  starter:      { month: 5,  annual: 5 },
  scholar:      { month: 15, annual: 15 },
};

const PLAN_LABELS: Record<string, string> = {
  essential: 'Essential',
  professional: 'Professional',
  starter: 'Starter',
  scholar: 'Scholar',
};

const COLORS: Record<string, string> = {
  Essential: '#3b82f6',
  Professional: '#7c3aed',
  Starter: '#10b981',
  Scholar: '#f59e0b',
};

interface RevenueChartProps {
  users: UserData[];
}

export function RevenueChart({ users }: RevenueChartProps) {
  const data = useMemo(() => {
    const planRevenue: Record<string, { label: string; count: number; mrr: number }> = {};

    for (const key of Object.keys(PLAN_PRICES)) {
      planRevenue[key] = { label: PLAN_LABELS[key], count: 0, mrr: 0 };
    }

    users.forEach(u => {
      if (!u.subscribed || !u.plan || !planRevenue[u.plan]) return;
      const prices = PLAN_PRICES[u.plan];
      const rate = u.billingInterval === 'year' ? prices.annual : prices.month;
      planRevenue[u.plan].count++;
      planRevenue[u.plan].mrr += rate;
    });

    return Object.values(planRevenue).filter(p => p.count > 0);
  }, [users]);

  return (
    <div className="chart-card chart-full-width">
      <h3>Revenue by Plan</h3>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" fontSize={12} tick={{ fill: '#374151' }} />
          <YAxis fontSize={11} tick={{ fill: '#6b7280' }} tickFormatter={v => `$${v}`} />
          <Tooltip formatter={(value: number) => [`$${value}/mo`, 'MRR']} />
          <Bar dataKey="mrr" name="Monthly Revenue" radius={[4, 4, 0, 0]}>
            {data.map((entry, i) => (
              <Cell key={i} fill={COLORS[entry.label] || '#6b7280'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
